import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  bookingActionsArea,
  bookingActionsPermissionList,
  clientProfileArea,
  clientProfilePermissionList,
  commissionSetupArea,
  commissionSetupPermissionList,
  fiscalReportsArea,
  fiscalReportsPermissionList,
  interfaceArea,
  interfacePermissionList,
  inventoryManagementArea,
  inventoryManagementPermissionList,
  guestCommunicationArea,
  guestCommunicationPermissionList,
  instructorScheduleArea,
  instructorSchedulePermissionList,
  manageActivityArea,
  manageActivityPermissionList,
  manageLocationsArea,
  manageLocationsPermissionList,
  reportsArea,
  reportsPermissionList,
  retailOperationsArea,
  retailOperationsPermissionList,
  retailReportsArea,
  retailReportsPermissionList,
  retailSetupArea,
  retailSetupPermissionList,
  retailTransactionsArea,
  retailTransactionsPermissionList,
  settingsAccessArea,
  settingsAccessPermissionList,
  utilitiesArea,
  utilitiesPermissionList,
} from "../../../constants/ReserveUserPermissionsFields";
import {
  reserveListAvailableHeadingTooltip,
  reserveUserPermissionsTooltipHeader,
  reserveUserPermissionsTooltipPermissionArea,
  reserveUserPermissionsTooltipRoleSelect,
} from "../../../constants/ReserveTooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../styles/popupStyle.css";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";
import useScrollToTop from "../../hooks/useScrollToTop";

function PermissionToggleGroup({
  areaLabel,
  options,
  value,
  onChange,
  idPrefix,
}: {
  areaLabel: string;
  options: readonly string[];
  value: string[];
  onChange: (next: string[]) => void;
  idPrefix: string;
}) {
  return (
    <Stack direction="column" width="100%" gap={1}>
      <FormLabel component="legend">
        {areaLabel}
        <Tooltip
          title={tooltipComponent(reserveUserPermissionsTooltipPermissionArea)}
        >
          <InfoIcon fontSize="small" />
        </Tooltip>
      </FormLabel>
      <ToggleButtonGroup
        value={value}
        onChange={(_event, next) => {
          onChange(
            Array.isArray(next) ? next : next != null ? [String(next)] : [],
          );
        }}
        aria-label={areaLabel}
        sx={{
          flexWrap: "wrap",
          gap: 0.75,
          justifyContent: "flex-start",
          "& .MuiToggleButtonGroup-grouped": {
            borderRadius: 1,
            m: 0,
          },
        }}
      >
        {options.map((opt) => (
          <ToggleButton
            key={opt}
            id={`${idPrefix}-${opt.replace(/\s+/g, "-").slice(0, 48)}`}
            value={opt}
            size="small"
            sx={{ textTransform: "none", py: 0.75 }}
          >
            {opt}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}

function readPermissionArrayFromRow(
  row: any,
  areaKey: string,
  canonicalList: readonly string[],
): string[] {
  if (Array.isArray(row[areaKey])) {
    return [...row[areaKey]];
  }
  if (row?.permissionDetail && typeof row.permissionDetail === "object") {
    const d = row.permissionDetail[areaKey];
    const lab = [...canonicalList];
    if (d?.mode === "full") return lab;
    if (d?.mode === "none") return [];
    return lab.filter((l) => Boolean(d?.items?.[l]));
  }
  if (typeof row[areaKey] === "boolean" && row[areaKey]) {
    return [...canonicalList];
  }
  return [];
}

const ReserveUserPermissions = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [selectedRoleCode, setSelectedRoleCode] = useState<string>("");
  const [bookingActionsSel, setBookingActionsSel] = useState<string[]>([]);
  const [guestCommunicationSel, setGuestCommunicationSel] = useState<string[]>(
    [],
  );
  const [instructorScheduleSel, setInstructorScheduleSel] = useState<string[]>(
    [],
  );
  const [manageActivitySel, setManageActivitySel] = useState<string[]>([]);
  const [manageLocationsSel, setManageLocationsSel] = useState<string[]>([]);
  const [reportsSel, setReportsSel] = useState<string[]>([]);
  const [settingsAccessSel, setSettingsAccessSel] = useState<string[]>([]);
  const [clientProfileSel, setClientProfileSel] = useState<string[]>([]);
  const [commissionSetupSel, setCommissionSetupSel] = useState<string[]>([]);
  const [fiscalReportsSel, setFiscalReportsSel] = useState<string[]>([]);
  const [interfaceSel, setInterfaceSel] = useState<string[]>([]);
  const [inventoryManagementSel, setInventoryManagementSel] = useState<
    string[]
  >([]);
  const [retailOperationsSel, setRetailOperationsSel] = useState<string[]>([]);
  const [retailReportsSel, setRetailReportsSel] = useState<string[]>([]);
  const [retailSetupSel, setRetailSetupSel] = useState<string[]>([]);
  const [retailTransactionsSel, setRetailTransactionsSel] = useState<string[]>(
    [],
  );
  const [utilitiesSel, setUtilitiesSel] = useState<string[]>([]);
  const [permissionProductTab, setPermissionProductTab] = useState<
    "activity" | "retail"
  >("activity");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [userPermissionsInformation, setUserPermissionsInformation] = useState<
    any[]
  >([]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [rolesList, setRolesList] = useState<any[]>([]);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  useEffect(() => {
    if (fieldValues) {
      const rawPermissions = fieldValues[ReservePropertyEnum.userPermissions];
      setUserPermissionsInformation(
        Array.isArray(rawPermissions) ? rawPermissions : [],
      );
      const raw = fieldValues[ReservePropertyEnum.roles];
      setRolesList(
        Array.isArray(raw)
          ? raw.filter((r: any) => r && r["Role Code"] && r.Active !== false)
          : [],
      );
    }
  }, []);

  useEffect(() => {
    data(userPermissionsInformation);
    editClicked &&
      setSelectedId(
        checkData(userPermissionsInformation, selectedId) ? selectedId : null,
      );
  }, [userPermissionsInformation]);

  const validateInput = () => {
    if (!selectedRoleCode) {
      setAlertMessage("Select a role");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (!rolesList.some((r: any) => r["Role Code"] === selectedRoleCode)) {
      setAlertMessage(
        "Selected role is not available. Add or activate it on the Roles page.",
      );
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  const editPermissions = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    if (list == null) {
      setSelectedRoleCode("");
      setBookingActionsSel([]);
      setGuestCommunicationSel([]);
      setInstructorScheduleSel([]);
      setManageActivitySel([]);
      setManageLocationsSel([]);
      setReportsSel([]);
      setSettingsAccessSel([]);
      setClientProfileSel([]);
      setCommissionSetupSel([]);
      setFiscalReportsSel([]);
      setInterfaceSel([]);
      setInventoryManagementSel([]);
      setRetailOperationsSel([]);
      setRetailReportsSel([]);
      setRetailSetupSel([]);
      setRetailTransactionsSel([]);
      setUtilitiesSel([]);
      setPermissionProductTab("activity");
      setEditClicked(false);
      return;
    }
    setSelectedRoleCode(list?.["Role Code"] ?? "");
    const row = list;
    if (!row) {
      return;
    }

    setBookingActionsSel(
      readPermissionArrayFromRow(
        row,
        bookingActionsArea,
        bookingActionsPermissionList,
      ),
    );
    setGuestCommunicationSel(
      readPermissionArrayFromRow(
        row,
        guestCommunicationArea,
        guestCommunicationPermissionList,
      ),
    );
    setInstructorScheduleSel(
      readPermissionArrayFromRow(
        row,
        instructorScheduleArea,
        instructorSchedulePermissionList,
      ),
    );
    setManageActivitySel(
      readPermissionArrayFromRow(
        row,
        manageActivityArea,
        manageActivityPermissionList,
      ),
    );
    setManageLocationsSel(
      readPermissionArrayFromRow(
        row,
        manageLocationsArea,
        manageLocationsPermissionList,
      ),
    );
    setReportsSel(
      readPermissionArrayFromRow(row, reportsArea, reportsPermissionList),
    );
    setSettingsAccessSel(
      readPermissionArrayFromRow(
        row,
        settingsAccessArea,
        settingsAccessPermissionList,
      ),
    );
    setClientProfileSel(
      readPermissionArrayFromRow(
        row,
        clientProfileArea,
        clientProfilePermissionList,
      ),
    );
    setCommissionSetupSel(
      readPermissionArrayFromRow(
        row,
        commissionSetupArea,
        commissionSetupPermissionList,
      ),
    );
    setFiscalReportsSel(
      readPermissionArrayFromRow(
        row,
        fiscalReportsArea,
        fiscalReportsPermissionList,
      ),
    );
    setInterfaceSel(
      readPermissionArrayFromRow(row, interfaceArea, interfacePermissionList),
    );
    setInventoryManagementSel(
      readPermissionArrayFromRow(
        row,
        inventoryManagementArea,
        inventoryManagementPermissionList,
      ),
    );
    setRetailOperationsSel(
      readPermissionArrayFromRow(
        row,
        retailOperationsArea,
        retailOperationsPermissionList,
      ),
    );
    setRetailReportsSel(
      readPermissionArrayFromRow(
        row,
        retailReportsArea,
        retailReportsPermissionList,
      ),
    );
    setRetailSetupSel(
      readPermissionArrayFromRow(
        row,
        retailSetupArea,
        retailSetupPermissionList,
      ),
    );
    setRetailTransactionsSel(
      readPermissionArrayFromRow(
        row,
        retailTransactionsArea,
        retailTransactionsPermissionList,
      ),
    );
    setUtilitiesSel(
      readPermissionArrayFromRow(row, utilitiesArea, utilitiesPermissionList),
    );
  };

  const deletePermissions = (id: number) => {
    setUserPermissionsInformation(
      deleteItem(userPermissionsInformation ?? [], id),
    );
  };

  const updatePermissions = () => {
    if (!validateInput()) return false;
    const selectedRoleName =
      (rolesList.find(
        (r: any) => String(r["Role Code"]) === String(selectedRoleCode),
      )?.["Role Name"] as string | undefined) ?? "";
    const peers = (userPermissionsInformation ?? []).filter(
      (item: any) => String(item.id) !== String(selectedId),
    );
    if (
      checkDuplicate(peers, "Role Code", selectedRoleCode) ||
      checkDuplicate(peers, "Role Name", selectedRoleName)
    ) {
      setAlertMessage(
        "Cannot have duplicate entries for the same role (Role Code or Role Name).",
      );
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setUserPermissionsInformation((prev: any) =>
      (Array.isArray(prev) ? prev : []).map((item: any) => {
        if (String(item.id) !== String(selectedId)) return item;
        const { permissionDetail: _omit, ...rest } = item;
        return { ...rest, ...payload, id: item.id };
      }),
    );
    editPermissions(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${ReservePropertyEnum.userPermissions} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addPermissions = () => {
    if (!validateInput()) {
      return;
    }
    const selectedRoleName =
      (rolesList.find(
        (r: any) => String(r["Role Code"]) === String(selectedRoleCode),
      )?.["Role Name"] as string | undefined) ?? "";
    const peers = userPermissionsInformation ?? [];
    if (
      checkDuplicate(peers, "Role Code", selectedRoleCode) ||
      checkDuplicate(peers, "Role Name", selectedRoleName)
    ) {
      setAlertMessage(
        "Cannot have duplicate entries for the same role (Role Code or Role Name).",
      );
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setUserPermissionsInformation((prev: any) =>
      updateData(
        Array.isArray(prev) && prev.length
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      ),
    );
    setLastUpdatedRecord(payload);
    editPermissions(null);
    setEditClicked(false);
    setSelectedId(null);
  };

  const payload = {
    "Role Code": selectedRoleCode,
    "Role Name":
      (rolesList.find(
        (r: any) => String(r["Role Code"]) === String(selectedRoleCode),
      )?.["Role Name"] as string | undefined) ?? "",
    [bookingActionsArea]: bookingActionsSel,
    [guestCommunicationArea]: guestCommunicationSel,
    [instructorScheduleArea]: instructorScheduleSel,
    [manageActivityArea]: manageActivitySel,
    [manageLocationsArea]: manageLocationsSel,
    [reportsArea]: reportsSel,
    [settingsAccessArea]: settingsAccessSel,
    [clientProfileArea]: clientProfileSel,
    [commissionSetupArea]: commissionSetupSel,
    [fiscalReportsArea]: fiscalReportsSel,
    [interfaceArea]: interfaceSel,
    [inventoryManagementArea]: inventoryManagementSel,
    [retailOperationsArea]: retailOperationsSel,
    [retailReportsArea]: retailReportsSel,
    [retailSetupArea]: retailSetupSel,
    [retailTransactionsArea]: retailTransactionsSel,
    [utilitiesArea]: utilitiesSel,
  };

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              {ReservePropertyEnum.userPermissions}
              <Tooltip
                title={tooltipComponent(reserveUserPermissionsTooltipHeader)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editPermissions(null);
                    setSelectedId(null);
                    setEditClicked(false);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editPermissions(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack direction="column" width="100%" gap="20px">
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Role
                <Tooltip
                  title={tooltipComponent(
                    reserveUserPermissionsTooltipRoleSelect,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Stack direction="row" width="100%" gap="5px" alignItems="center">
                <FormControl fullWidth disabled={!rolesList.length}>
                  <InputLabel id="reserve-perm-role-label">Role</InputLabel>
                  <Select
                    labelId="reserve-perm-role-label"
                    id="reserve-perm-role"
                    label="Role"
                    displayEmpty
                    value={selectedRoleCode || ""}
                    onChange={(e) =>
                      setSelectedRoleCode(e.target.value as string)
                    }
                    renderValue={(v) => {
                      if (!v) return "";
                      const r = rolesList.find(
                        (x: any) => String(x["Role Code"]) === String(v),
                      );
                      return r
                        ? `${r["Role Name"]} (${r["Role Code"]})`
                        : String(v);
                    }}
                    inputProps={{ "aria-label": "Role" }}
                  >
                    {!rolesList.length ? (
                      <MenuItem value="" disabled>
                        No roles defined — add roles on the Roles page first
                      </MenuItem>
                    ) : (
                      rolesList.map((r: any) => (
                        <MenuItem key={r["Role Code"]} value={r["Role Code"]}>
                          {r["Role Name"]} ({r["Role Code"]})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            <Stack
              className="form-two-col-row"
              direction="row"
              alignItems="center"
              gap={0.5}
            >
              <Typography variant="caption" color="text.secondary">
                Roles are maintained on the Roles page.
              </Typography>
            </Stack>

            <FormLabel component="legend" id="reserve-perm-sections-label">
              Permission categories
              <Tooltip
                title={tooltipComponent(
                  reserveUserPermissionsTooltipPermissionArea,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>

            <Tabs
              value={permissionProductTab}
              onChange={(_e, v) =>
                setPermissionProductTab(v as "activity" | "retail")
              }
              aria-labelledby="reserve-perm-sections-label"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label="Activity" value="activity" />
              <Tab label="Retail" value="retail" />
            </Tabs>

            {permissionProductTab === "activity" && (
              <Stack direction="column" width="100%" gap={2} pt={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Activity-related permissions for this role.
                </Typography>
                <PermissionToggleGroup
                  areaLabel={bookingActionsArea}
                  options={bookingActionsPermissionList}
                  value={bookingActionsSel}
                  onChange={setBookingActionsSel}
                  idPrefix="reserve-perm-booking-actions"
                />
                <PermissionToggleGroup
                  areaLabel={guestCommunicationArea}
                  options={guestCommunicationPermissionList}
                  value={guestCommunicationSel}
                  onChange={setGuestCommunicationSel}
                  idPrefix="reserve-perm-guest-communication"
                />
                <PermissionToggleGroup
                  areaLabel={instructorScheduleArea}
                  options={instructorSchedulePermissionList}
                  value={instructorScheduleSel}
                  onChange={setInstructorScheduleSel}
                  idPrefix="reserve-perm-instructor-schedule"
                />
                <PermissionToggleGroup
                  areaLabel={manageActivityArea}
                  options={manageActivityPermissionList}
                  value={manageActivitySel}
                  onChange={setManageActivitySel}
                  idPrefix="reserve-perm-manage-activity"
                />
                <PermissionToggleGroup
                  areaLabel={manageLocationsArea}
                  options={manageLocationsPermissionList}
                  value={manageLocationsSel}
                  onChange={setManageLocationsSel}
                  idPrefix="reserve-perm-manage-locations"
                />
                <PermissionToggleGroup
                  areaLabel={reportsArea}
                  options={reportsPermissionList}
                  value={reportsSel}
                  onChange={setReportsSel}
                  idPrefix="reserve-perm-reports"
                />
                <PermissionToggleGroup
                  areaLabel={settingsAccessArea}
                  options={settingsAccessPermissionList}
                  value={settingsAccessSel}
                  onChange={setSettingsAccessSel}
                  idPrefix="reserve-perm-settings-access"
                />
              </Stack>
            )}

            {permissionProductTab === "retail" && (
              <Stack direction="column" width="100%" gap={2} pt={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Retail-related permissions for this role (commission, fiscal
                  reports, interface, inventory, POS retail, reports, setup,
                  transactions, utilities, and client profile).
                </Typography>
                <PermissionToggleGroup
                  areaLabel={commissionSetupArea}
                  options={commissionSetupPermissionList}
                  value={commissionSetupSel}
                  onChange={setCommissionSetupSel}
                  idPrefix="reserve-perm-retail-commission"
                />
                <PermissionToggleGroup
                  areaLabel={fiscalReportsArea}
                  options={fiscalReportsPermissionList}
                  value={fiscalReportsSel}
                  onChange={setFiscalReportsSel}
                  idPrefix="reserve-perm-retail-fiscal-reports"
                />
                <PermissionToggleGroup
                  areaLabel={interfaceArea}
                  options={interfacePermissionList}
                  value={interfaceSel}
                  onChange={setInterfaceSel}
                  idPrefix="reserve-perm-retail-interface"
                />
                <PermissionToggleGroup
                  areaLabel={inventoryManagementArea}
                  options={inventoryManagementPermissionList}
                  value={inventoryManagementSel}
                  onChange={setInventoryManagementSel}
                  idPrefix="reserve-perm-retail-inventory"
                />
                <PermissionToggleGroup
                  areaLabel={retailOperationsArea}
                  options={retailOperationsPermissionList}
                  value={retailOperationsSel}
                  onChange={setRetailOperationsSel}
                  idPrefix="reserve-perm-retail-operations"
                />
                <PermissionToggleGroup
                  areaLabel={retailReportsArea}
                  options={retailReportsPermissionList}
                  value={retailReportsSel}
                  onChange={setRetailReportsSel}
                  idPrefix="reserve-perm-retail-reports"
                />
                <PermissionToggleGroup
                  areaLabel={retailSetupArea}
                  options={retailSetupPermissionList}
                  value={retailSetupSel}
                  onChange={setRetailSetupSel}
                  idPrefix="reserve-perm-retail-setup"
                />
                <PermissionToggleGroup
                  areaLabel={retailTransactionsArea}
                  options={retailTransactionsPermissionList}
                  value={retailTransactionsSel}
                  onChange={setRetailTransactionsSel}
                  idPrefix="reserve-perm-retail-transactions"
                />
                <PermissionToggleGroup
                  areaLabel={utilitiesArea}
                  options={utilitiesPermissionList}
                  value={utilitiesSel}
                  onChange={setUtilitiesSel}
                  idPrefix="reserve-perm-retail-utilities"
                />
                <PermissionToggleGroup
                  areaLabel={clientProfileArea}
                  options={clientProfilePermissionList}
                  value={clientProfileSel}
                  onChange={setClientProfileSel}
                  idPrefix="reserve-perm-retail-client-profile"
                />
              </Stack>
            )}
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() =>
                selectedId ? updatePermissions() : addPermissions()
              }
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
              Permission Set
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography
              variant="h6"
              component="div"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {ReservePropertyEnum.userPermissions} Available
              <Tooltip
                title={tooltipComponent(reserveListAvailableHeadingTooltip)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="column" gap="10px">
              {Array.isArray(userPermissionsInformation) &&
              userPermissionsInformation.length > 0 ? (
                <DraggableItemList
                  itemData={userPermissionsInformation}
                  onOrderUpdate={(order) =>
                    setUserPermissionsInformation(order)
                  }
                  onEdit={(info) => {
                    editPermissions(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deletePermissions(id)}
                  code="Role Code"
                  name="Role Name"
                />
              ) : (
                <Typography variant="body2">
                  No {ReservePropertyEnum.userPermissions} information available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default ReserveUserPermissions;
